/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React from 'react';
import { connect, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { func, object, string } from 'prop-types';
import { Button, useMediaQuery } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import Overlay from './Partials/Overlay';
import SubscriptionTandC from './SubscriptionTandC';
import { isEmpty } from '../../../pages/Account/utils/object';
import GenericSkeleton from '../GraphqlSkeletonComponents/GenericSkeleton';
import { addToCart } from '../../../../state/ducks/AddToCart/AddToCart-Actions';
import { getFeatureFlags } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import ResponsiveImage from '../GraphqlCommonComponents/ResponsiveImage/ResponsiveImage';
import subscriptionConfig from '../../../helpers/common/subscription/subscription-helpers';
import PassportBannerContainer from '../GraphqlProductPage/Partials/StandardProductsPage/PassportBannerContainer';
import PassportBannerUI from '../../../pages/Checkout/containers/CheckoutFloral/Partials/Passport/PassportBannerUI/PassportBannerUI';

const useStyles = makeStyles((theme) => ({
    container: {
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        padding: '50px 0',
        '@media screen and (max-width: 768px)': {
            padding: '50px 20px',
            flexDirection: 'column',
            alignItems: 'center',
        },
        '& .MuiDivider-root': {
            maxWidth: '1300px',
            margin: '0 auto',
        },
        '& .MuiButton-root': {
            color: theme.palette.white,
            textTransform: 'uppercase',
            fontSize: '13px',
            margin: '20px 0',
            padding: '15px 60px',
            fontWeight: '700',
            width: '100%',
            backgroundColor: theme.palette.primaryButton,
            '-webkit-font-smoothing': 'antialiased',
            '&:hover': {
                backgroundColor: theme.palette.primaryButton,
            },
        },
        '& #order_summary': {
            fontSize: '1.4em',
            fontFamily: 'ivypresto-text,PlayfairDisplayBold, serif',
        },
    },
    imageContainer: {
        width: '35%',
        minWidth: '350px',
        maxWidth: '500px',
        '@media screen and (max-width: 768px)': {
            width: '100%',
            minWidth: '280px',
        },
    },
    images: {
        width: '100%',
        height: '350px',
        objectFit: 'cover',
        '@media screen and (max-width: 768px)': {
            width: '100%',
        },
    },
    orderDetails: {
        width: '30%',
        minWidth: '350px',
        padding: '0 0 0 40px',
        textAlign: 'left',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        '@media screen and (max-width: 768px)': {
            width: '100%',
            padding: 0,
            minWidth: '280px',
            maxWidth: '350px',
            alignItems: 'center',
        },
        '& .MuiPaper-elevation1': {
            boxShadow: 'none',
        },
        '& #order_subHeading': {
            fontSize: '0.8em',
        },
        '& #order_total': {
            padding: '20px 0',
            display: 'flex',
            alignItems: 'start',
            '&>span': {
                fontSize: '2.5em',
                fontFamily: 'LatoMedium,sans-serif',
                margin: '-10px 10px',
            },
        },
        '& .MuiPaper-root': {
            marginBottom: 0,
        },
    },
    formContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: 'auto',
        '& div': {
            width: '100%',
            maxWidth: '400px',
            padding: '10px',
            textAlign: 'center',
            textTransform: 'uppercase',
            border: '1px solid rgb(0,0,0, 0.3)',
            margin: '0 10px',
        },
    },
    priceLoader: {
        margin: '0 10px !important',
    },
}));

const OrderSummary = ({
    addToCartData, actionAddToCart, trackEvent, product, selectedVariant, selectedPrice, brand, variantsDetail,
}) => {
    const classes = useStyles();
    const selectedSku = addToCartData?.selectedSku || {};

    const frequencyValue = addToCartData?.reqObj?.subscription?.interval;
    const frequencyText = frequencyValue ? subscriptionConfig.subscription_interval?.[frequencyValue] : '';

    const durationValue = addToCartData?.reqObj?.subscription?.duration;
    const durationText = durationValue ? subscriptionConfig.subscription_duration?.[durationValue] : '';

    const featureFlags = useSelector(getFeatureFlags);
    const cartIconLabel = featureFlags['cart-icon-label']?.label || 'Cart';

    const handleAddToCartClick = () => {
        // tracking
        trackEvent('Subscriptions Experience', 'click – Step 4', 'Add to Cart');

        actionAddToCart(
            addToCartData.reqObj,
            addToCartData.history,
            addToCartData.deliveryType,
            addToCartData.categoryId,
            addToCartData.productPartNumber,
            addToCartData.skipAddons,
            addToCartData.date,
            addToCartData.categoryName,
            addToCartData.categoryPath,
            addToCartData.isPassportBundleItem,
            addToCartData.movieSelected,
            addToCartData.selectedSku,
            addToCartData.passportBundle,
            addToCartData.handleMiniCartModalClick,
            addToCartData.enableMinicart,
            addToCartData.close,
            'true',
            // locationType.name,
        );
    };

    const renderImage = (item) => {
        const variantDetail = variantsDetail?.find((x) => item.id === x.variant_sku);
        const { image } = item;
        const responsiveImagePath = () => {
            const extension = (image?.name || '').includes('_gif') ? '.gif' : 'x.jpg';
            return `${image?.path + image?.name + extension}`;
        };

        let imagePath = responsiveImagePath();
        if (variantDetail?.variant_image?.url) imagePath = variantDetail.variant_image.url;
        else if (variantsDetail?.length > 0 && variantsDetail[0].variant_image?.url) imagePath = variantsDetail[0].variant_image.url;

        return (
            <div className={classes.imageContainer}>
                <ResponsiveImage
                    alt={image?.name}
                    fullWidth
                    className={classes.images}
                    path={imagePath}
                    params={{ desktop: 'quality=100', mobile: 'quality=100' }}
                />
            </div>
        );
    };
    const isMobile = useMediaQuery('(max-width: 768px)');

    return (
        <div className={classes.container} id="order_summary_container">
            {isMobile && <div id="order_summary" style={{ paddingBottom: '10px' }}>Order Summary:</div>}
            {isEmpty(selectedSku) ? renderImage(product) : renderImage(selectedSku)}
            <div className={classes.orderDetails}>
                {!isMobile && <div id="order_summary">Order Summary:</div>}
                <div id="order_subHeading" style={{ paddingTop: isMobile && '10px' }}><strong>{selectedVariant} {frequencyText && ` | ${frequencyText}`} {durationText && ` | ${durationText}`}</strong></div>
                <div id="order_total">Total:
                    {selectedPrice ? <span>${selectedPrice}</span> : <GenericSkeleton height="50px" width="50px" className={classes.priceLoader} />}
                    Per Delivery
                </div>
                <PassportBannerContainer selectedSku={selectedSku}>
                    <PassportBannerUI
                        invokedFrom="subscriptionsExp"
                        bannerOnPage="subscriptionsExp"
                    />
                </PassportBannerContainer>
                <Button data-testid="add-to-cart" onClick={handleAddToCartClick}>Add to {cartIconLabel}</Button>
                <SubscriptionTandC brand={brand} trackEvent={trackEvent} />
            </div>
            {isEmpty(addToCartData?.reqObj?.subscription || {}) && <Overlay />}
        </div>
    );
};

const mapDispatchToProps = (dispatch) => ({
    actionAddToCart: bindActionCreators(addToCart, dispatch),
});

OrderSummary.propTypes = {
    addToCartData: object.isRequired,
    trackEvent: func.isRequired,
    actionAddToCart: func.isRequired,
    product: object.isRequired,
    selectedVariant: string.isRequired,
    selectedPrice: string.isRequired,
    brand: string.isRequired,
    variantsDetail: object.isRequired,
};

export default connect(null, mapDispatchToProps)(OrderSummary);
